import { formatDistanceStrict, formatDistanceToNowStrict } from 'date-fns';
import { Check, ChevronDown, TriangleAlert, X } from 'lucide-react';
import React, {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    type OutreachScheduleConflict,
    OutreachScheduleConflictDialog,
    parseOutreachScheduleConflict,
} from '@apps/web/components/outreaches/outreach_schedule_conflict_dialog';
import { CaregiverNameButton } from '@apps/web/components/phoebe_chat_view/tool_calls/caregiver_memory_tool_ui';
import { type RecommendationBatchAggregateDecision } from '@apps/web/components/phoebe_chat_view/transcript_types';
import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { useCanPlaceCalls } from '@apps/web/hooks/use_can_place_calls';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useTrainingMode } from '@apps/web/hooks/use_training_mode';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { stripEntityReferences } from '@apps/web/lib/entity_references';
import {
    getApiErrorDetail,
    parseKnownApiError,
} from '@apps/web/lib/known_api_errors';
import {
    createStartOutreachTimeout,
    getOutreachSequence,
    getOutreachStartedReport,
    STILL_STARTING_REPORT_TEXT,
} from '@apps/web/lib/outreach_started_report';
import { cn } from '@apps/web/lib/utils';
import {
    useJoinedOutreach,
    useRecommendationBatchCandidates,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type JoinedOutreachRead } from '@libraries/typescript/api_client';
import { ApiError } from '@libraries/typescript/api_client/error';
import {
    type OutreachStatus,
    type RecommendationBatchCandidate,
} from '@libraries/typescript/api_client/schema_types';
import { assert } from '@libraries/typescript/utilities';

type StartOutreachState = 'idle' | 'starting' | 'started';
type RecommendationBatchDisplayCandidate = Omit<
    RecommendationBatchCandidate,
    'created_at' | 'updated_at'
> & {
    readonly created_at: string | null;
    readonly updated_at: string | null;
};

const ACTIVE_REFRESH_INTERVAL_MS = 1500;
// Gentler cadence for watching the outreach after the batch completes: the
// only change we're waiting for is someone starting or filling the outreach.
const OUTREACH_STATUS_WATCH_INTERVAL_MS = 5000;

// A terminal outreach (filled, ended, cancelled) locks the prompt's button
// with the reason. A RUNNING outreach does not: asking for more caregivers
// stages new suggestions on it, and the confirm endpoint supports adding
// people to an in-progress outreach (already-contacted caregivers are
// skipped server-side), so the prompt re-arms to contact the new ones.
function startOutreachUnavailableLabel(
    status: OutreachStatus | null | undefined,
): string | null {
    switch (status) {
        case null:
        case undefined:
        case 'pending':
        case 'in_progress':
        case 'paused':
            return null;
        case 'filled':
            return 'Outreach filled';
        case 'unfilled':
            return 'Outreach ended';
        case 'cancelled':
            return 'Outreach cancelled';
        default:
            return assert.never(status);
    }
}

// A running outreach re-arms the prompt with add-people wording instead of
// the first-start wording.
function outreachIsRunning(status: OutreachStatus | null | undefined): boolean {
    return status === 'in_progress' || status === 'paused';
}

// Stable default so a carousel without aggregate decisions doesn't re-derive
// its memo chain (and re-fire its fan-out report effect) on every render.
const NO_AGGREGATE_DECISIONS: ReadonlyArray<RecommendationBatchAggregateDecision> =
    [];

const SUGGESTION_STAGED_STATUSES = new Set(['added', 'coverage_extended']);
const AGGREGATE_CANDIDATE_STATUSES = new Set([
    'queued',
    'running',
    'completed',
    'failed',
]);

function normalizeAggregateStatus(status: string): string {
    return AGGREGATE_CANDIDATE_STATUSES.has(status) ? status : 'failed';
}

function normalizeAggregateSuggestion(
    suggestion: string | undefined,
): string | null {
    if (
        suggestion === 'already_suggested' ||
        SUGGESTION_STAGED_STATUSES.has(suggestion ?? '')
    ) {
        return suggestion ?? null;
    }
    return null;
}

function candidateKey(candidate: RecommendationBatchDisplayCandidate): string {
    return candidate.caregiver_id ?? candidate.decision_id;
}

// Decline reasoning is contractually prefixed with a hard-conflict marker so
// the backend can validate the veto; the red "Declined" chip already carries
// that meaning here, so drop the marker from the coordinator-facing text.
const HARD_CONFLICT_REASONING_PREFIXES = [
    'Hard caregiver conflict:',
    'Hard memory conflict:',
];

function displayReasoning(raw: string): string {
    const stripped = stripEntityReferences(raw).trim();
    for (const prefix of HARD_CONFLICT_REASONING_PREFIXES) {
        if (stripped.toLowerCase().startsWith(prefix.toLowerCase())) {
            const body = stripped.slice(prefix.length).trim();
            if (body) {
                return body.charAt(0).toUpperCase() + body.slice(1);
            }
        }
    }
    return stripped;
}

// A multi-shift fan-out renders one carousel per shift, but the coordinator
// should see a single "Start Outreach" prompt: under the last carousel, only
// once every batch in the group has finished. Carousels report their live
// status here; the tail carousel reads the group aggregate to gate its
// prompt.
type FanoutGroupBatchReport = {
    readonly complete: boolean;
    readonly approvedCaregiverKeys: ReadonlyArray<string>;
};

// Reports are keyed by a per-carousel-instance id, not by batchId: a
// transcript can carry both a `recommendation-batch` entry and a
// `subagent-result` marker for the same batch, and two same-batch carousels
// sharing one slot would overwrite each other's status in an unbounded
// setState ping-pong (WEB-G8).
type RecommendationFanoutGroup = {
    readonly report: (reportId: string, status: FanoutGroupBatchReport) => void;
    readonly unregister: (reportId: string) => void;
    readonly allComplete: boolean;
    readonly approvedCount: number;
};

const RecommendationFanoutGroupContext =
    createContext<RecommendationFanoutGroup | null>(null);

export function RecommendationFanoutGroupProvider({
    children,
}: {
    readonly children: ReactNode;
}) {
    const [reports, setReports] = useState<
        ReadonlyMap<string, FanoutGroupBatchReport>
    >(new Map());
    const report = useCallback(
        (reportId: string, status: FanoutGroupBatchReport) => {
            setReports((previous) => {
                const existing = previous.get(reportId);
                if (
                    existing &&
                    existing.complete === status.complete &&
                    existing.approvedCaregiverKeys.length ===
                        status.approvedCaregiverKeys.length &&
                    existing.approvedCaregiverKeys.every(
                        (key, index) =>
                            key === status.approvedCaregiverKeys[index],
                    )
                ) {
                    return previous;
                }
                const next = new Map(previous);
                next.set(reportId, status);
                return next;
            });
        },
        [],
    );
    const unregister = useCallback((reportId: string) => {
        setReports((previous) => {
            if (!previous.has(reportId)) {
                return previous;
            }
            const next = new Map(previous);
            next.delete(reportId);
            return next;
        });
    }, []);
    const value = useMemo(() => {
        const all = [...reports.values()];
        // A caregiver approved on several shifts counts once toward the
        // group prompt; the joined-outreach staged list the button sends
        // is deduplicated the same way.
        const approved = new Set<string>();
        for (const batchReport of all) {
            for (const key of batchReport.approvedCaregiverKeys) {
                approved.add(key);
            }
        }
        return {
            report,
            unregister,
            allComplete:
                all.length > 0 &&
                all.every((batchReport) => batchReport.complete),
            approvedCount: approved.size,
        };
    }, [reports, report, unregister]);
    return (
        <RecommendationFanoutGroupContext.Provider value={value}>
            {children}
        </RecommendationFanoutGroupContext.Provider>
    );
}

/**
 * Live list of per-caregiver fork decisions for one fan-out batch.
 *
 * Rows appear top to bottom in the order their decision forks finish (not
 * recommendation rank), so the coordinator watches the evaluation stream in.
 * Polls the batch read model while any fork is still running, then settles
 * on the final decision set.
 */
export function RecommendationDecisionCarousel({
    batchId,
    candidateCount,
    aggregateDecisions = NO_AGGREGATE_DECISIONS,
    organizationIdOverride,
    onCaregiverClick,
    offerStartOutreach = true,
    groupPromptSlot = true,
    showTraceProgress = false,
}: {
    readonly batchId: string;
    readonly candidateCount: number | null;
    readonly aggregateDecisions?: ReadonlyArray<RecommendationBatchAggregateDecision>;
    // Admin trace detail can inspect a run owned by an organization other
    // than the viewer's currently selected organization.
    readonly organizationIdOverride?: string | null;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    // False once the conversation has moved past this batch (the
    // coordinator sent another message), which retires the stale
    // "Start Outreach" prompt.
    readonly offerStartOutreach?: boolean;
    // In a multi-shift fan-out only the last carousel renders the shared
    // "Start Outreach" prompt; siblings keep contributing status through
    // the fan-out group context but stay prompt-less.
    readonly groupPromptSlot?: boolean;
    // Admin trace detail exposes queued/running rows and lifecycle timing.
    // Coordinator chat keeps its result-first presentation.
    readonly showTraceProgress?: boolean;
}) {
    const selectedOrganizationId = useOrganizationId();
    const { timezone } = useOrganizationTimezone();
    const organizationId =
        organizationIdOverride === undefined
            ? selectedOrganizationId
            : organizationIdOverride;
    // Decoration only (an explainer line under the prompt), so no isReady
    // gating: worst case the line pops in after the org loads.
    const { isInTrainingMode } = useTrainingMode({
        organizationId,
        reason: 'recommendation-carousel-start-outreach-explainer',
    });
    const { data } = useRecommendationBatchCandidates(organizationId, batchId, {
        adminTrace: showTraceProgress,
    });
    const complete = data?.complete === true || aggregateDecisions.length > 0;
    const group = useContext(RecommendationFanoutGroupContext);
    const [startState, setStartState] = useState<StartOutreachState>('idle');
    // Captured at click time so the prompt's presentation freezes with the
    // click. The batch hooks share one SWR key and revalidate on focus, so
    // after a first start the status flips to in_progress under the
    // just-answered prompt — deriving from the live status would rewrite it
    // into the "more caregivers" wording with a different count.
    const [startedWhileRunning, setStartedWhileRunning] = useState(false);
    // Inside a fan-out group, only the designated tail carousel prompts,
    // and only once every sibling batch is complete; the prompt then counts
    // unique approved caregivers across the whole group. Standalone (no
    // provider), this reduces to the single-batch behavior.
    const promptReady = group
        ? groupPromptSlot && group.allComplete
        : groupPromptSlot;
    // While this carousel's prompt is live and the outreach can still take
    // caregivers (pending or running), keep polling (slower) so a fill or
    // cancel from anywhere else — the call-out page, another coordinator —
    // greys the button out without a reload. Both hooks share one SWR key,
    // so the watch results land in `data` too.
    const watchingOutreachStatus =
        complete &&
        offerStartOutreach &&
        promptReady &&
        !showTraceProgress &&
        startState === 'idle' &&
        (data?.outreach_status === 'pending' ||
            outreachIsRunning(data?.outreach_status));
    const { data: refreshed } = useRecommendationBatchCandidates(
        organizationId,
        complete && !watchingOutreachStatus ? null : batchId,
        {
            refreshIntervalMs: complete
                ? OUTREACH_STATUS_WATCH_INTERVAL_MS
                : ACTIVE_REFRESH_INTERVAL_MS,
            adminTrace: showTraceProgress,
        },
    );
    const batch = refreshed ?? data;
    const aggregateFallback = useMemo<
        ReadonlyArray<RecommendationBatchDisplayCandidate>
    >(
        () =>
            aggregateDecisions.map((decision, index) => ({
                decision_id: `aggregate-${batchId}-${index}`,
                caregiver_id: decision.caregiver_id,
                caregiver_name: null,
                original_rank: index + 1,
                status: normalizeAggregateStatus(decision.status),
                terminal: true,
                should_add: decision.should_add ?? null,
                reasoning: decision.reasoning ?? decision.error ?? null,
                suggestion_status: normalizeAggregateSuggestion(
                    decision.suggestion,
                ),
                failure_kind: decision.error ? 'error' : null,
                created_at: null,
                updated_at: null,
            })),
        [aggregateDecisions, batchId],
    );
    const effectiveBatch = useMemo(
        () =>
            (batch === undefined || batch.candidates.length === 0) &&
            aggregateFallback.length > 0
                ? {
                      ...(batch ?? {
                          batch_id: batchId,
                          outreach_id: null,
                          total_count: aggregateFallback.length,
                          terminal_count: aggregateFallback.length,
                          complete: true,
                      }),
                      candidates: aggregateFallback,
                      total_count: aggregateFallback.length,
                      terminal_count: aggregateFallback.length,
                      complete: true,
                  }
                : batch,
        [aggregateFallback, batch, batchId],
    );

    // Freeze each row's position at the moment its decision first appears
    // so rows enter at the bottom and never reshuffle on later polls.
    const arrivalOrderRef = useRef<Array<string>>([]);
    const decided = useMemo(() => {
        const candidates = effectiveBatch?.candidates ?? [];
        const decidedByKey = new Map<
            string,
            RecommendationBatchDisplayCandidate
        >();
        for (const candidate of candidates) {
            if (candidate.terminal) {
                decidedByKey.set(candidateKey(candidate), candidate);
            }
        }
        const order = arrivalOrderRef.current;
        for (const key of decidedByKey.keys()) {
            if (!order.includes(key)) {
                order.push(key);
            }
        }
        return order
            .map((key) => decidedByKey.get(key))
            .filter(
                (candidate): candidate is RecommendationBatchDisplayCandidate =>
                    candidate !== undefined,
            );
    }, [effectiveBatch]);
    const pendingCandidates = useMemo(() => {
        if (!showTraceProgress) {
            return [];
        }
        return [...(effectiveBatch?.candidates ?? [])]
            .filter((candidate) => !candidate.terminal)
            .sort(
                (left, right) =>
                    Number(right.status === 'running') -
                        Number(left.status === 'running') ||
                    (left.original_rank ?? Number.MAX_SAFE_INTEGER) -
                        (right.original_rank ?? Number.MAX_SAFE_INTEGER),
            );
    }, [effectiveBatch, showTraceProgress]);

    // Report live status into the fan-out group (when one is mounted) so
    // the tail carousel can wait for every sibling before prompting.
    const approvedCaregiverKeys = useMemo(
        () =>
            decided
                .filter((candidate) => candidate.should_add === true)
                .map(candidateKey),
        [decided],
    );
    // Depend on the stable callbacks, never on the group value object: its
    // identity changes with every registration, and an effect keyed on it
    // would unregister/re-register in an infinite ping-pong.
    const reportToGroup = group?.report;
    const unregisterFromGroup = group?.unregister;
    // Report under a per-instance id, not batchId: two carousels can render
    // the same batch (a `recommendation-batch` entry plus a
    // `subagent-result` marker), and sharing a slot lets them overwrite each
    // other's status every render — an unbounded setState ping-pong.
    const fanoutReportId = useId();
    useEffect(() => {
        if (!reportToGroup || !offerStartOutreach) {
            return;
        }
        reportToGroup(fanoutReportId, {
            complete,
            approvedCaregiverKeys,
        });
    }, [
        reportToGroup,
        offerStartOutreach,
        fanoutReportId,
        complete,
        approvedCaregiverKeys,
    ]);
    useEffect(() => {
        if (!unregisterFromGroup) {
            return;
        }
        if (!offerStartOutreach) {
            unregisterFromGroup(fanoutReportId);
        }
        return () => unregisterFromGroup(fanoutReportId);
    }, [unregisterFromGroup, offerStartOutreach, fanoutReportId]);

    // Start the outreach directly from the chat: one confirm-suggestions-v2
    // POST with no caregiver list, which starts every staged suggestion on
    // the outreach server-side — the same endpoint the call-out page's
    // start bar uses. No LLM turn is involved.
    const outreachId = batch?.outreach_id ?? null;
    // The prompt must name the set the button actually submits — every staged
    // suggestion on the outreach, not just this batch's approvals.
    const { data: stagedOutreach } = useJoinedOutreach(
        offerStartOutreach ? organizationId : null,
        offerStartOutreach ? outreachId : null,
        { reason: 'chat-recommendation-batch-staged-count' },
    );
    const stagedCaregiverCount = useMemo(
        () =>
            (stagedOutreach?.suggested_caregivers ?? []).filter(
                (suggestion) => suggestion.covered_shift_ids.length > 0,
            ).length,
        [stagedOutreach],
    );
    // Same gating as the callout page: only ask about voice capability when
    // the playbook has a voice step to describe.
    const hasVoiceStep = (stagedOutreach?.steps ?? []).some(
        (step) => step.step_type === 'voice',
    );
    const canPlaceCalls = useCanPlaceCalls(
        hasVoiceStep ? organizationId : null,
    );
    // Whether the prompt (and its button) targets only newly staged
    // caregivers: live status while the prompt is unanswered, the
    // click-time capture afterwards so a first start's own status flip to
    // in_progress cannot rewrite the answered prompt.
    const contactsNewCaregivers =
        startState === 'idle'
            ? outreachIsRunning(batch?.outreach_status)
            : startedWhileRunning;
    const [scheduleConflict, setScheduleConflict] =
        useState<OutreachScheduleConflict | null>(null);
    const [conflictShifts, setConflictShifts] = useState<
        JoinedOutreachRead['shifts']
    >([]);
    const handleStartOutreach = useCallback(
        async (acknowledgedConflicts: Record<string, Array<string>> = {}) => {
            if (!organizationId || !outreachId) {
                return;
            }
            setStartedWhileRunning(outreachIsRunning(batch?.outreach_status));
            setStartState('starting');
            const startTimeout = createStartOutreachTimeout();
            // The conflict dialog labels its shifts from whatever joined
            // snapshot we have; the start itself no longer needs one.
            setConflictShifts(stagedOutreach?.shifts ?? []);
            try {
                // One small POST: omitting caregiver_shift_ids tells the
                // server to start every staged suggestion itself — the same
                // set the joined outreach would have echoed back. The old
                // fetch-then-start shape re-downloaded the whole joined
                // outreach on click, and that extra round-trip was the only
                // part of this button that could fail when the page's other
                // requests were fine.
                await getApiClient().post({
                    url: '/organizations/{organization_id}/outreaches/{outreach_id}/confirm-suggestions-v2',
                    pathParams: {
                        organization_id: organizationId,
                        outreach_id: outreachId,
                    },
                    body: {
                        skip_conflict_check: false,
                        acknowledged_conflicts: acknowledgedConflicts,
                        // This button lives inside the agent conversation, so an
                        // "outreach started" poke would spend an LLM turn
                        // narrating the click back to the coordinator.
                        suppress_agent_notification: true,
                    },
                    reason: 'chat-recommendation-batch-start-outreach',
                    signal: startTimeout.signal,
                });
                setScheduleConflict(null);
                // On a running outreach the click adds the newly staged
                // caregivers (already-contacted ones are skipped
                // server-side), so the first-start report would overclaim.
                toast.success(
                    outreachIsRunning(batch?.outreach_status)
                        ? 'Contacting the new caregivers.'
                        : stagedOutreach
                          ? getOutreachStartedReport({
                                caregiverCount: stagedCaregiverCount,
                                sequence: getOutreachSequence(
                                    stagedOutreach.steps,
                                    { canPlaceCalls },
                                ),
                            })
                          : 'Outreach started',
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
                setStartState('started');
            } catch (error) {
                const conflict = parseOutreachScheduleConflict(error);
                if (conflict) {
                    setScheduleConflict(conflict);
                    setStartState('idle');
                    return;
                }
                const isTimeout =
                    error instanceof ApiError && error.isAbortError;
                const knownError = parseKnownApiError(error);
                toast.error(
                    isTimeout
                        ? STILL_STARTING_REPORT_TEXT
                        : knownError?.kind === 'caregivers_skip_listed'
                          ? knownError.message
                          : (getApiErrorDetail(error) ??
                            'We couldn’t start the outreach. Try again.'),
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
                setStartState('idle');
            } finally {
                startTimeout.clear();
            }
        },
        [
            organizationId,
            outreachId,
            canPlaceCalls,
            stagedOutreach,
            stagedCaregiverCount,
            batch?.outreach_status,
        ],
    );

    return (
        <React.Fragment>
            <RecommendationDecisionCarouselView
                totalCount={effectiveBatch?.total_count || candidateCount || 0}
                complete={effectiveBatch?.complete ?? complete}
                decided={decided}
                pendingCandidates={pendingCandidates}
                showTraceProgress={showTraceProgress}
                onCaregiverClick={onCaregiverClick}
                startOutreach={
                    outreachId &&
                    offerStartOutreach &&
                    !showTraceProgress &&
                    promptReady
                        ? {
                              state: startState,
                              onStart: handleStartOutreach,
                              // First start submits every staged suggestion,
                              // so the prompt counts the staged set once it
                              // loads (a genuine zero must survive as zero);
                              // until then this batch group's approvals are
                              // the best number. When the click targets a
                              // RUNNING outreach only the new approvals get
                              // dialed, so the staged total would overcount —
                              // keep the group's fresh approvals instead.
                              count: contactsNewCaregivers
                                  ? group?.approvedCount
                                  : stagedOutreach
                                    ? stagedCaregiverCount
                                    : group?.approvedCount,
                              // Training mode spells out what the button does so
                              // a scheduler new to Phoebe knows texts go out.
                              showTrainingExplainer: isInTrainingMode,
                              outreachStatus: batch?.outreach_status ?? null,
                              contactsNewCaregivers,
                          }
                        : undefined
                }
            />
            <OutreachScheduleConflictDialog
                conflict={scheduleConflict}
                shifts={conflictShifts}
                timezone={timezone}
                isSubmitting={startState === 'starting'}
                onCancel={() => setScheduleConflict(null)}
                onConfirm={(acknowledgedConflicts) => {
                    void handleStartOutreach(acknowledgedConflicts);
                }}
            />
        </React.Fragment>
    );
}

function decisionDuration(
    candidate: RecommendationBatchDisplayCandidate,
): string {
    if (candidate.created_at === null || candidate.updated_at === null) {
        return 'duration unavailable';
    }
    const start = new Date(candidate.created_at);
    const end = new Date(candidate.updated_at);
    if (
        !Number.isFinite(start.getTime()) ||
        !Number.isFinite(end.getTime()) ||
        end < start
    ) {
        return 'duration unavailable';
    }
    return formatDistanceStrict(start, end);
}

function pendingDuration(
    candidate: RecommendationBatchDisplayCandidate,
): string {
    if (candidate.created_at === null || candidate.updated_at === null) {
        return 'timing unavailable';
    }
    const since =
        candidate.status === 'running'
            ? new Date(candidate.updated_at)
            : new Date(candidate.created_at);
    if (!Number.isFinite(since.getTime())) {
        return 'timing unavailable';
    }
    return formatDistanceToNowStrict(since);
}

function PendingDecisionRow({
    candidate,
    onCaregiverClick,
}: {
    readonly candidate: RecommendationBatchDisplayCandidate;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const running = candidate.status === 'running';
    return (
        <div className="bg-muted/20 flex items-center gap-2 px-3 py-2">
            {running ? (
                <Spinner size="xs" tone="muted" className="shrink-0" />
            ) : (
                <span className="bg-muted-foreground/35 size-2.5 shrink-0 rounded-full" />
            )}
            <span
                className="max-w-[45%] min-w-0 shrink-0 truncate text-xs font-medium"
                title={candidate.caregiver_name ?? undefined}
            >
                <CaregiverNameButton
                    caregiverId={candidate.caregiver_id}
                    name={candidate.caregiver_name ?? 'Caregiver'}
                    onCaregiverClick={onCaregiverClick}
                />
            </span>
            <span className="text-muted-foreground min-w-0 flex-1 text-[11px]">
                {running
                    ? 'Worker is preparing or evaluating'
                    : 'Waiting for a swarm worker'}
            </span>
            <span className="text-muted-foreground shrink-0 text-[10px] tabular-nums">
                {running ? 'Evaluating' : 'Queued'} for{' '}
                {pendingDuration(candidate)}
            </span>
        </div>
    );
}

/**
 * Presentational half of the carousel: renders one snapshot of decisions.
 */
export function RecommendationDecisionCarouselView({
    totalCount,
    complete,
    decided,
    pendingCandidates = [],
    showTraceProgress = false,
    onCaregiverClick,
    startOutreach,
}: {
    readonly totalCount: number;
    readonly complete: boolean;
    readonly decided: Array<RecommendationBatchDisplayCandidate>;
    readonly pendingCandidates?: Array<RecommendationBatchDisplayCandidate>;
    readonly showTraceProgress?: boolean;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly startOutreach?: {
        readonly state: StartOutreachState;
        readonly onStart: () => void;
        // Unique approved caregivers across the whole fan-out group;
        // omitted for a standalone batch, which counts its own approvals.
        readonly count?: number;
        // Training mode adds a plain-language line about what starting
        // actually does (texts now, calls for non-responders).
        readonly showTrainingExplainer?: boolean;
        // Live status of the outreach the button would start; a terminal
        // status (filled/ended/cancelled) renders the button greyed out
        // with the reason.
        readonly outreachStatus?: OutreachStatus | null;
        // True when the button targets only newly staged caregivers on an
        // already-running outreach. The container freezes this at click
        // time so a first start's own status flip cannot rewrite the
        // answered prompt into the "more caregivers" wording.
        readonly contactsNewCaregivers?: boolean;
    };
}) {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    // Rows the coordinator has opened to read the full decision reasoning.
    const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(
        new Set(),
    );
    const toggleExpanded = useCallback((key: string) => {
        setExpandedKeys((previous) => {
            const next = new Set(previous);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);
    // Which frosted scroll edges are showing: an edge appears only when rows
    // actually continue past it, so the blur itself is the "more to scroll"
    // affordance.
    const [scrollEdges, setScrollEdges] = useState({
        top: false,
        bottom: false,
    });
    const updateScrollEdges = useCallback(() => {
        const element = scrollRef.current;
        if (!element) {
            return;
        }
        const top = element.scrollTop > 4;
        const bottom =
            element.scrollHeight - element.scrollTop - element.clientHeight > 4;
        setScrollEdges((previous) =>
            previous.top === top && previous.bottom === bottom
                ? previous
                : { top, bottom },
        );
    }, []);
    useEffect(() => {
        const element = scrollRef.current;
        if (!element) {
            return;
        }
        const distanceFromEndPx =
            element.scrollHeight - element.scrollTop - element.clientHeight;
        // Follow the stream only while the user is near the bottom; never
        // yank them back while they are reading earlier decisions.
        if (distanceFromEndPx < 80) {
            element.scrollTo({
                top: element.scrollHeight,
                behavior: 'smooth',
            });
        }
        updateScrollEdges();
    }, [decided.length, updateScrollEdges]);

    const approvedCount = decided.filter(
        (candidate) => candidate.should_add === true,
    ).length;
    const promptApprovedCount = startOutreach?.count ?? approvedCount;
    const startUnavailableLabel = startOutreachUnavailableLabel(
        startOutreach?.outreachStatus,
    );
    const promptOutreachRunning = startOutreach?.contactsNewCaregivers === true;
    const declinedCount = decided.filter(
        (candidate) => candidate.should_add === false,
    ).length;
    const failedCount = decided.filter(
        (candidate) => candidate.status !== 'completed',
    ).length;
    const stagedCount = decided.filter(
        (candidate) =>
            candidate.suggestion_status !== null &&
            candidate.suggestion_status !== undefined &&
            SUGGESTION_STAGED_STATUSES.has(candidate.suggestion_status),
    ).length;
    const alreadySuggestedCount = decided.filter(
        (candidate) => candidate.suggestion_status === 'already_suggested',
    ).length;
    const pendingCount = Math.max(totalCount - decided.length, 0);
    const runningCount = pendingCandidates.filter(
        (candidate) => candidate.status === 'running',
    ).length;
    const queuedCount = pendingCandidates.filter(
        (candidate) => candidate.status === 'queued',
    ).length;
    const hasDetailedPendingStatus =
        showTraceProgress && runningCount + queuedCount === pendingCount;
    const isEvaluating = !complete && pendingCount > 0;
    const completionSummary = !complete
        ? null
        : stagedCount > 0
          ? `${stagedCount} caregiver${stagedCount === 1 ? '' : 's'} added to the outreach${
                alreadySuggestedCount > 0
                    ? ` · ${alreadySuggestedCount} already suggested`
                    : ''
            }`
          : alreadySuggestedCount > 0
            ? `No new caregivers added — all ${alreadySuggestedCount} good fit${alreadySuggestedCount === 1 ? '' : 's'} were already suggested`
            : `${approvedCount} good fit${approvedCount === 1 ? '' : 's'}`;

    return (
        // w-0 + min-w-full: contribute zero intrinsic width so the panel can
        // never stretch chat-column ancestors, while still rendering at the
        // full column width. Without this, nested flex parents grow to fit
        // the nowrap row text and the panel runs off screen.
        <div className="w-0 min-w-full space-y-2">
            <div className="border-border/50 bg-background/70 overflow-hidden rounded-lg border shadow-xs backdrop-blur-md">
                <div className="border-border/40 border-b px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                        <div className="flex min-w-0 items-center gap-2">
                            {isEvaluating ? (
                                <Spinner
                                    size="xs"
                                    tone="muted"
                                    className="shrink-0"
                                />
                            ) : null}
                            <p className="text-foreground text-xs font-medium">
                                {isEvaluating
                                    ? `Evaluating ${totalCount} caregiver${totalCount === 1 ? '' : 's'}…`
                                    : `Evaluated ${totalCount} caregiver${totalCount === 1 ? '' : 's'}`}
                            </p>
                        </div>
                        <div className="text-muted-foreground flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px]">
                            <span className="flex items-center gap-1.5">
                                <span className="size-1.5 rounded-full bg-emerald-500" />
                                {approvedCount} good fit
                                {approvedCount === 1 ? '' : 's'}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="size-1.5 rounded-full bg-red-400" />
                                {declinedCount} weak fit
                                {declinedCount === 1 ? '' : 's'}
                            </span>
                            {failedCount > 0 ? (
                                <span className="flex items-center gap-1.5">
                                    <span className="size-1.5 rounded-full bg-amber-500" />
                                    {failedCount} failed
                                </span>
                            ) : null}
                            {pendingCount > 0 ? (
                                <span className="flex items-center gap-1.5">
                                    <span className="bg-muted-foreground/40 size-1.5 rounded-full" />
                                    {hasDetailedPendingStatus
                                        ? `${runningCount} evaluating · ${queuedCount} queued`
                                        : `${pendingCount} pending`}
                                </span>
                            ) : null}
                        </div>
                    </div>
                    {completionSummary ? (
                        <p className="text-muted-foreground mt-1 text-[11px]">
                            {completionSummary}
                            {declinedCount > 0
                                ? ` · ${declinedCount} declined`
                                : ''}
                            {failedCount > 0 ? ` · ${failedCount} failed` : ''}
                        </p>
                    ) : null}
                </div>
                {decided.length > 0 || pendingCandidates.length > 0 ? (
                    <div className="relative">
                        <div
                            ref={scrollRef}
                            onScroll={updateScrollEdges}
                            className="divide-border/40 max-h-56 divide-y overflow-y-auto"
                        >
                            {decided.map((candidate) => {
                                const key = candidateKey(candidate);
                                const failed = candidate.status !== 'completed';
                                const approved =
                                    !failed && candidate.should_add === true;
                                // Reasoning is written by the model and can
                                // carry raw [[type:uuid]] entity tokens and
                                // the internal hard-conflict prefix; render
                                // the coordinator-safe form.
                                const reasoning = failed
                                    ? candidate.failure_kind === 'timeout'
                                        ? 'Provider evaluation timed out.'
                                        : 'Evaluation failed before recording a decision.'
                                    : candidate.reasoning
                                      ? displayReasoning(candidate.reasoning)
                                      : null;
                                const expanded = expandedKeys.has(key);
                                return (
                                    <div
                                        key={key}
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={expanded}
                                        onClick={() => toggleExpanded(key)}
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === 'Enter' ||
                                                event.key === ' '
                                            ) {
                                                event.preventDefault();
                                                toggleExpanded(key);
                                            }
                                        }}
                                        className={cn(
                                            'recommendation-decision-row-enter cursor-pointer px-3 py-1.5',
                                            failed
                                                ? 'bg-amber-500/5'
                                                : approved
                                                  ? 'bg-emerald-500/5'
                                                  : 'bg-red-500/5',
                                        )}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span
                                                className={cn(
                                                    'recommendation-decision-badge-pop flex size-4 shrink-0 items-center justify-center rounded-full',
                                                    failed
                                                        ? 'bg-amber-500/15'
                                                        : approved
                                                          ? 'bg-emerald-500/15'
                                                          : 'bg-red-500/15',
                                                )}
                                            >
                                                {failed ? (
                                                    <TriangleAlert className="size-2.5 text-amber-600" />
                                                ) : approved ? (
                                                    <Check className="size-2.5 text-emerald-600" />
                                                ) : (
                                                    <X className="size-2.5 text-red-600" />
                                                )}
                                            </span>
                                            <span
                                                className="max-w-[45%] min-w-0 shrink-0 truncate text-xs font-medium"
                                                title={
                                                    candidate.caregiver_name ??
                                                    undefined
                                                }
                                            >
                                                <CaregiverNameButton
                                                    caregiverId={
                                                        candidate.caregiver_id ??
                                                        null
                                                    }
                                                    name={
                                                        candidate.caregiver_name ??
                                                        'Caregiver'
                                                    }
                                                    onCaregiverClick={
                                                        onCaregiverClick
                                                    }
                                                />
                                            </span>
                                            {!expanded ? (
                                                <span
                                                    className={cn(
                                                        'text-muted-foreground min-w-0 flex-1 truncate text-[11px]',
                                                        (failed ||
                                                            !candidate.reasoning) &&
                                                            'italic',
                                                    )}
                                                >
                                                    {reasoning ??
                                                        'No reasoning recorded'}
                                                </span>
                                            ) : (
                                                <span className="min-w-0 flex-1" />
                                            )}
                                            <span className="flex shrink-0 items-center gap-1.5 text-[10px] font-medium">
                                                {candidate.suggestion_status ===
                                                'already_suggested' ? (
                                                    <span className="text-muted-foreground">
                                                        Already suggested
                                                    </span>
                                                ) : null}
                                                <span
                                                    className={
                                                        failed
                                                            ? 'text-amber-600'
                                                            : approved
                                                              ? 'text-emerald-600'
                                                              : 'text-red-600'
                                                    }
                                                >
                                                    {failed
                                                        ? 'Failed'
                                                        : approved
                                                          ? 'Good fit'
                                                          : 'Weak fit'}
                                                    {showTraceProgress
                                                        ? ` · ${decisionDuration(candidate)}`
                                                        : ''}
                                                </span>
                                            </span>
                                            <ChevronDown
                                                className={cn(
                                                    'text-muted-foreground/60 size-3 shrink-0 transition-transform duration-200',
                                                    expanded && 'rotate-180',
                                                )}
                                            />
                                        </div>
                                        {expanded ? (
                                            <p
                                                className={cn(
                                                    'text-muted-foreground mt-1 pr-5 pl-6 text-[11px] leading-relaxed',
                                                    (failed ||
                                                        !candidate.reasoning) &&
                                                        'italic',
                                                )}
                                            >
                                                {reasoning ??
                                                    'No reasoning recorded'}
                                            </p>
                                        ) : null}
                                    </div>
                                );
                            })}
                            {pendingCandidates.map((candidate) => (
                                <PendingDecisionRow
                                    key={candidateKey(candidate)}
                                    candidate={candidate}
                                    onCaregiverClick={onCaregiverClick}
                                />
                            ))}
                        </div>
                        {/* Frosted edges: off-screen rows blur out under these
                        overlays, hinting at the scroll direction that has
                        more content. */}
                        <div
                            className={cn(
                                'pointer-events-none absolute inset-x-0 top-0 h-10 backdrop-blur-[2px] transition-opacity duration-200',
                                'from-background/80 bg-gradient-to-b to-transparent',
                                '[mask-image:linear-gradient(to_bottom,black,transparent)]',
                                scrollEdges.top ? 'opacity-100' : 'opacity-0',
                            )}
                        />
                        <div
                            className={cn(
                                'pointer-events-none absolute inset-x-0 bottom-0 h-10 backdrop-blur-[2px] transition-opacity duration-200',
                                'from-background/80 bg-gradient-to-t to-transparent',
                                '[mask-image:linear-gradient(to_top,black,transparent)]',
                                scrollEdges.bottom
                                    ? 'opacity-100'
                                    : 'opacity-0',
                            )}
                        />
                    </div>
                ) : (
                    <p className="text-muted-foreground px-3 py-2 text-[11px]">
                        Waiting for the first decisions…
                    </p>
                )}
            </div>
            {complete && startOutreach && promptApprovedCount > 0 ? (
                <div className="space-y-2">
                    <p className="text-foreground text-sm">
                        {startUnavailableLabel
                            ? `I found ${promptApprovedCount} caregiver${promptApprovedCount === 1 ? '' : 's'}.`
                            : promptOutreachRunning
                              ? `I found ${promptApprovedCount} more caregiver${promptApprovedCount === 1 ? '' : 's'}. Want me to contact them too?`
                              : `I found ${promptApprovedCount} caregiver${promptApprovedCount === 1 ? '' : 's'}. Want me to contact them?`}
                    </p>
                    {startOutreach.showTrainingExplainer &&
                    !startUnavailableLabel ? (
                        <p className="text-muted-foreground text-xs">
                            Starting reaches these caregivers for real — usually
                            by text first, with phone calls for anyone who
                            doesn’t reply.
                        </p>
                    ) : null}
                    <Button
                        type="button"
                        // Never pass onStart as the handler directly: React
                        // would hand it the click event, which the container
                        // treats as its acknowledged-conflicts argument — a
                        // DOM-node-bearing object that breaks the request
                        // body's JSON serialization before anything is sent.
                        onClick={() => startOutreach.onStart()}
                        disabled={
                            startOutreach.state !== 'idle' ||
                            startUnavailableLabel !== null
                        }
                    >
                        {startOutreach.state === 'starting'
                            ? 'Contacting…'
                            : startOutreach.state === 'started'
                              ? promptOutreachRunning
                                  ? 'New caregivers contacted'
                                  : 'Outreach started'
                              : (startUnavailableLabel ??
                                (promptOutreachRunning
                                    ? 'Contact new caregivers'
                                    : 'Start Outreach'))}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
